import React, {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton, Modal} from '@optimaxdev/design-system';

import {useSelector} from 'react-redux';

import {
    SUBSCRIPTION_CONSENT_EVENT,
    SUBSCRIPTION_CONSENT_SHOWED,
} from 'constants/analytics/events/checkout';

import {useHasGlassesCare} from 'desktop/hooks/useHasGlassesCare/useHasGlassesCare';
import {getIsVista} from 'desktop/pages/checkout/store/selectors/orderReviewItems/orderReviewItems';

import {subscriptionConsentData} from './constants';
import {SaveSubscriptionType} from './types';
import {saveSubscriptionConsent} from './useSaveSubscriptionConsent/useSaveSubscriptionConsent';

import s from './subscriptionModal.scss';

export type PropsType = {
    isOpen: boolean;
    closePopup: () => void;
    handleSubmit: () => void;
};

const getSubscriptionType = (isVista: boolean, isGlassesCare: boolean): SaveSubscriptionType => {
    if (isVista) return 'vista_plus';
    if (isGlassesCare) return 'glasses_care_plus';
    return 'cl_subscription';
};

/**
 * Popup Wrap
 * @param {PropsType} props - component props
 * @returns {React.ReactElement} - Subscription Consent Modal
 */
export const SubscriptionModal: React.FC<PropsType> = ({
    closePopup,
    handleSubmit,
    isOpen,
}: PropsType): React.ReactElement => {
    const isVista = useSelector(getIsVista);
    const isGlassesCare = useHasGlassesCare();

    const subscriptionType = getSubscriptionType(isVista, isGlassesCare);

    const {eventAction, label, text, type} = subscriptionConsentData[subscriptionType];

    useEffect(() => {
        if (isOpen) {
            sendDesktopAnalyticsEvent(SUBSCRIPTION_CONSENT_SHOWED, {eventAction, label}, false);
        }
    }, [eventAction, isOpen, label]);

    const sendAnalyticByClicking = (eventType: string) => {
        sendDesktopAnalyticsEvent(SUBSCRIPTION_CONSENT_EVENT, {eventAction, eventType, label});
    };

    const handleAgreeClick = () => {
        handleSubmit();
        saveSubscriptionConsent(type);
        closePopup();
        sendAnalyticByClicking('Agree');
    };

    return (
        <Modal
            onRequestClose={() => {
                closePopup();
                sendAnalyticByClicking('Close By X');
            }}
            isOpen={isOpen}
            overlay="dark"
        >
            <div className={s.popupContent}>
                <h2
                    className={s.title}
                >{`${subscriptionType === 'glasses_care_plus' ? 'GlassesCare+ ' : ''}Subscription Consent`}</h2>
                <p className={s.text}>{text}</p>
                <div className={s.buttonsContainer}>
                    <CtaButton
                        onClick={() => {
                            closePopup();
                            sendAnalyticByClicking('Cancel');
                        }}
                        colorName="secondary"
                        size="medium"
                        variant="ghost"
                        isFull
                    >
                        Cancel
                    </CtaButton>
                    <CtaButton
                        colorName="secondary"
                        onClick={handleAgreeClick}
                        size="medium"
                        variant="painted"
                        isFull
                    >
                        Agree & Continue to Payment
                    </CtaButton>
                </div>
            </div>
        </Modal>
    );
};
